import type React from "react";
import { useState } from "react";
import Dropdown from "../dropdown/Dropdown";
import Icon from "../icon/Icon";
import ListItem from "../list/ListItem";

export interface AccordionListItemAction {
  text: string;
  leadingIcon?: string;
  critical?: boolean;
  onClick: (e: React.MouseEvent) => void;
}

export interface AccordionListItemGroupAction {
  text: string;
  leadingIcon?: string;
  critical?: boolean;
  onClick: () => void;
}

export type AccordionListItemActions =
  | AccordionListItemGroupAction[]
  | React.ReactNode;

export interface AccordionListItemData {
  id: string | number;
  [key: string]: unknown;
}

export interface AccordionListItemGroup<T extends AccordionListItemData> {
  groupKey: string;
  title: string;
  subtitle?: string;
  items: T[];
  actions?: AccordionListItemActions; // Can be dropdown items OR custom React node
  isDefaultExpanded?: boolean;
  customContent?: React.ReactNode; // Optional custom content instead of items
}

export interface AccordionListItemProps<T extends AccordionListItemData> {
  groups: AccordionListItemGroup<T>[];
  onItemClick?: (item: T) => void;
  getItemPrimaryContent?: (item: T) => string;
  getItemSecondaryContent?: (item: T) => string;
  getItemActions?: (item: T) => AccordionListItemAction[];
  className?: string;
  expandedGroups?: Set<string>;
  onToggleGroup?: (groupKey: string) => void;
  controlled?: boolean; // Whether expansion state is controlled externally
}

function AccordionListItem<T extends AccordionListItemData>({
  groups,
  onItemClick,
  getItemPrimaryContent,
  getItemSecondaryContent,
  getItemActions,
  className = "",
  expandedGroups: controlledExpandedGroups,
  onToggleGroup,
  controlled = false,
}: AccordionListItemProps<T>) {
  const [internalExpandedGroups, setInternalExpandedGroups] = useState<
    Set<string>
  >(
    () =>
      new Set(groups.filter((g) => g.isDefaultExpanded).map((g) => g.groupKey)),
  );

  const expandedGroups = controlled
    ? controlledExpandedGroups || new Set()
    : internalExpandedGroups;

  const toggleGroup = (groupKey: string) => {
    if (controlled && onToggleGroup) {
      onToggleGroup(groupKey);
    } else if (!controlled) {
      setInternalExpandedGroups((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(groupKey)) {
          newSet.delete(groupKey);
        } else {
          newSet.add(groupKey);
        }
        return newSet;
      });
    }
  };

  if (!groups || groups.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {groups.map((group) => {
        const isExpanded = expandedGroups.has(group.groupKey);

        return (
          <div
            key={group.groupKey}
            className="border border-border-subdued rounded-xl overflow-hidden"
          >
            {/* Group Header */}
            <div className="flex items-center justify-between p-3 bg-surface-subdued hover:bg-surface-hover transition-colors">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.groupKey)}
                  className="flex items-center gap-2 text-left hover:text-text-default transition-colors flex-1 min-w-0"
                >
                  <Icon
                    name={isExpanded ? "arrowhead-up-md" : "arrowhead-down-md"}
                    className="shrink-0 transition-transform"
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="body-md-bold line-clamp-1 break-words">
                      {group.title}
                    </span>
                    {group.subtitle && (
                      <span className="body-sm text-text-subdued line-clamp-1">
                        {group.subtitle}
                      </span>
                    )}
                  </div>
                </button>
              </div>

              {/* Group Actions - Dropdown or Custom Content */}
              {group.actions && (
                <div className="flex items-center gap-3">
                  {Array.isArray(group.actions)
                    ? // Render dropdown for array of actions
                      group.actions.length > 0 && (
                        <Dropdown
                          variant="shapeless"
                          size="sm"
                          trailingIcon="more"
                          closeOnClick
                          items={group.actions.map((action) => ({
                            text: action.text,
                            leadingIcon: action.leadingIcon as any,
                            critical: action.critical,
                            onClick: action.onClick,
                          }))}
                        />
                      )
                    : // Render custom React node
                      group.actions}
                </div>
              )}
            </div>

            {/* Group Content */}
            {isExpanded && (
              <div className="p-3 space-y-2 bg-surface-default">
                {group.customContent
                  ? // Render custom content if provided
                    group.customContent
                  : // Render default list items
                    group.items.map((item) => (
                      <ListItem
                        key={item.id}
                        primaryContent={getItemPrimaryContent?.(item) || ""}
                        secondaryContent={getItemSecondaryContent?.(item)}
                        onClick={
                          onItemClick ? () => onItemClick(item) : undefined
                        }
                        classNames={{
                          wrapper: "border border-border-subdued rounded-lg",
                        }}
                        action={
                          getItemActions && getItemActions(item).length > 0 ? (
                            <Dropdown
                              variant="shapeless"
                              size="sm"
                              trailingIcon="more"
                              closeOnClick
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              items={getItemActions(item).map((action) => ({
                                text: action.text,
                                leadingIcon: action.leadingIcon as any,
                                critical: action.critical,
                                onClick: (e) => {
                                  e.stopPropagation();
                                  action.onClick(e);
                                },
                              }))}
                            />
                          ) : undefined
                        }
                      />
                    ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default AccordionListItem;
